import { compact } from 'lodash'
import { useEffect, useMemo, useRef } from 'react'

import getCustomerAssets from '~/auth/assets'

type TitleComponent = string | null | undefined

/**
 * Sets the page title using the provided title components. Components are combined with a dash
 * separator. The first component is always "Synop".
 *
 * This hook must stay free of Redux / Statsig / RTK Query — it is used on auth surfaces
 * (e.g. `AuthPageLogo`) and in unit tests that do not mount those providers. Org-name
 * title suffixes live in `OrgDocumentTitleSuffix` inside the authenticated layout.
 *
 * This hook uses a MutationObserver on <head> to ensure the client-side title takes precedence
 * over Next.js metadata updates during client-side navigation. We observe <head> rather than
 * <title> because Next.js may replace the entire <title> element.
 *
 * @example
 * usePageTitle('Dashboard') // Sets the page title to "Synop - Dashboard"
 * usePageTitle('Dashboard', 'Users') // Sets the page title to "Synop - Dashboard - Users"
 * usePageTitle('Chargers', chargerName) // Sets the page title to "Synop - Chargers - {chargerName}"
 * usePageTitle('Chargers', null) // Sets the page title to "Synop - Chargers"
 */
export default function usePageTitle(...components: TitleComponent[]) {
  const topLevelTitle = useMemo(() => {
    const asset = getCustomerAssets()
    return (asset?.title ?? process.env.NEXT_PUBLIC_PAGE_TITLE) || 'Synop'
  }, [])

  const title = compact([topLevelTitle, ...components]).join(' - ')
  const sanitizedTitle = sanitizeTitle(title)
  const titleRef = useRef(sanitizedTitle)
  titleRef.current = sanitizedTitle

  useEffect(() => {
    // Set the title immediately. OrgDocumentTitleSuffix (authenticated layout) may
    // append " · {org}" afterward — treat that as still matching our base title so
    // the two MutationObservers do not fight.
    document.title = sanitizedTitle

    // Observe <head> for any mutations - this catches both content changes to <title>
    // and complete replacement of the <title> element by Next.js
    const observer = new MutationObserver(() => {
      // Browsers canonicalize `document.title` (notably whitespace), so always compare
      // using the same sanitization to avoid a set/observe infinite loop.
      const current = sanitizeTitle(document.title)
      const expected = titleRef.current
      if (current === expected || current.startsWith(`${expected} · `)) {
        return
      }
      document.title = expected
    })

    observer.observe(document.head, {
      childList: true,
      characterData: true,
      subtree: true
    })

    return () => {
      observer.disconnect()
    }
  }, [sanitizedTitle])
}

function sanitizeTitle(title: string) {
  // Cross-browser canonicalization for `document.title`.
  //
  // Spec basis: WHATWG Infra “strip and collapse ASCII whitespace”:
  // replace consecutive ASCII whitespace with a single space, then trim
  // leading/trailing ASCII whitespace.
  //
  // In practice, some engines also trim/collapse a broader set of Unicode
  // whitespace when the title is reflected through `document.title`, so we
  // collapse all JS-regex whitespace (\s) to ensure stable equality and avoid
  // MutationObserver loops.
  const normalized = typeof title.normalize === 'function' ? title.normalize('NFC') : title

  return (
    normalized
      // Replace NUL with U+FFFD (common UA behavior when reflecting text).
      .replace(/\u0000/gu, '\uFFFD')
      // Collapse all whitespace runs to a single ASCII space.
      .replace(/\s+/gu, ' ')
      // Trim leading/trailing whitespace.
      .trim()
  )
}
